import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { PlugsConnected, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "../context/AuthContext";

export const AREA_META = {
  contabilita: { name: "Contabilità", color: "#38BDF8" },
  paghe: { name: "Paghe", color: "#6366F1" },
  tesoreria: { name: "Tesoreria", color: "#E9A23B" },
  posta: { name: "Posta", color: "#F97316" },
  risorice: { name: "Risorice", color: "#E05E3F" },
  produzione: { name: "Produzione", color: "#9F1239" },
  sohelp: { name: "So.Help", color: "#10B981" },
  paqway: { name: "PAQ.WAY", color: "#F43F5E" },
};

export const useAppData = (endpoint) => {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(
    () => api.get(endpoint).then(({ data: d }) => setData(d)).catch(() => {}),
    [endpoint]
  );
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post("/admin/apps/refresh");
      for (let i = 0; i < 20; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: d } = await api.get(endpoint);
        if (!d.refreshing) { setData(d); break; }
      }
      toast.success("Dati aggiornati dalle webapp");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setRefreshing(false);
  };

  return { data, refreshing, refresh, load };
};

export const StatusBadge = ({ status }) => {
  if (status === "connected")
    return (
      <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-emerald-400/10 border border-emerald-400/30 text-emerald-400 text-[10px] font-semibold" data-testid="app-status-connected">
        <CheckCircle size={11} weight="fill" /> Collegata
      </span>
    );
  if (status === "error")
    return (
      <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-semibold" data-testid="app-status-error">
        <XCircle size={11} weight="fill" /> Errore
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-full bg-white/5 border border-white/15 text-white/45 text-[10px] font-semibold" data-testid="app-status-pending">
      <PlugsConnected size={11} /> In attesa di collegamento
    </span>
  );
};

export const KpiChip = ({ kpi }) => (
  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid={`kpi-${kpi.key || kpi.label}`}>
    <p className="text-[10px] tracking-[0.15em] uppercase text-white/35 truncate">{kpi.label}</p>
    <p className="font-heading text-2xl font-bold tracking-tighter mt-1">
      {typeof kpi.value === "number" ? kpi.value.toLocaleString("it-IT") : kpi.value}
      {kpi.unit ? <span className="text-xs font-normal text-white/40 ml-1">{kpi.unit}</span> : null}
    </p>
  </div>
);

export const MultiSeriesChart = ({ lines }) => {
  const dates = [...new Set(lines.flatMap((l) => (l.points || []).map((p) => p.date)))].sort();
  if (!dates.length) return null;
  const max = Math.max(...lines.flatMap((l) => (l.points || []).map((p) => p.value)), 1);
  const W = 600, H = 150, P = 4;
  const toPts = (line) => {
    const map = Object.fromEntries((line.points || []).map((p) => [p.date, p.value]));
    return dates
      .map((d, i) => `${P + (i / Math.max(dates.length - 1, 1)) * (W - 2 * P)},${H - P - ((map[d] ?? 0) / max) * (H - 2 * P)}`)
      .join(" ");
  };
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-40" preserveAspectRatio="none" data-testid="apps-cross-chart">
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1={P} x2={W - P} y1={H * f} y2={H * f} stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
        ))}
        {lines.map((l) => (
          <polyline key={l.label} points={toPts(l)} fill="none" stroke={l.color}
            strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" opacity="0.9" />
        ))}
      </svg>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
        {lines.map((l) => (
          <span key={l.label} className="inline-flex items-center gap-1.5 text-xs text-white/60">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: l.color }} /> {l.label}
          </span>
        ))}
      </div>
    </>
  );
};

export const AreaCardShell = ({ area, status, error, updatedAt, children, testId, endpointHint }) => {
  const meta = AREA_META[area];
  return (
    <div data-testid={testId}
      className="p-6 rounded-lg bg-surface border border-surface-border border-l-4 animate-fade-up"
      style={{ borderLeftColor: meta.color }}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="font-heading text-lg font-semibold tracking-tight" style={{ color: meta.color }}>{meta.name}</h2>
        <StatusBadge status={status} />
      </div>
      {status === "connected" ? (
        <>
          {children}
          {updatedAt && (
            <p className="text-[10px] text-white/30 mt-3">
              Aggiornato: {new Date(updatedAt).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}
            </p>
          )}
        </>
      ) : status === "error" ? (
        <p className="text-xs text-red-400">{error || "Errore nel recupero dati"}</p>
      ) : (
        <p className="text-xs text-white/40">
          La webapp non espone ancora l'endpoint <code className="text-white/60">{endpointHint}</code>. Incolla le istruzioni nella sua chat per collegarla.
        </p>
      )}
    </div>
  );
};

export const SpecLink = () => (
  <p className="text-[11px] text-white/35 mt-8">
    Per collegare una webapp: scarica le{" "}
    <a href="/SPEC_DATI_ANALISI_MARKETING.md" download data-testid="spec-dati-link"
      className="text-sky-400 hover:underline">istruzioni tecniche</a>{" "}
    e incollale nella chat della webapp. I dati si aggiornano automaticamente ogni giorno alle 05:00 UTC.
  </p>
);
